import { FC, useState, useMemo, useEffect, useRef } from "react"
import cx from "classnames"
import dayjs from "dayjs"

// Reusable components from the single-org dashboard
import MobileDashboardFiltersDrawer from "@/pages/Dashboard/features/MobileDashboardFiltersDrawer"
import SingleGraphView from "@/pages/Dashboard/features/SingleGraphView"
import EmptyGraphView from "@/pages/Dashboard/features/EmptyGraphView"
import ProviderAnalyticsSummary, {
  getProviderAnalyticsRows,
} from "@/pages/Dashboard/features/ProviderAnalyticsSummary"
import Loader from "@/components/Loader"
import ErrorMessage from "@/components/ErrorMessage"
import IconButton from "@/components/IconButton"
import FilterIcon from "@/components/icons/FilterIcon"

// Hooks and API calls
import { useDashboard } from "@/pages/Dashboard/hooks/useDashboard" // Reuse filter logic
import { useGetAdminDashboardReportingQuery } from "@/redux/services/adminOrganizationsApi" // Admin-specific API
import { formatDateToISOString } from "@/utils/dates" // Date formatting utility
import { POLLING_INTERVAL } from "@/constants" // Polling constant if needed
import type { DashboardPayload } from "@/redux/services/organizationApi" // Type for filters payload

// Ant Design components for explicit filters
import { Select as AntSelect } from "antd" // Only need Select from antd now
// Custom DatePicker component used in single-org dashboard
import DatePicker from "@/components/DatePicker"
import CalendarIcon from "@/components/icons/CalendarIcon"
// Import the necessary type for GROUPING_VALUES
import type { DashboardGrouping } from "@/pages/Dashboard/hooks/utils"
// Import the filter dropdown components
import IndicatorDropdown from "@/pages/Dashboard/features/IndicatorDropdown"
import ClassificationsDropdown from "@/components/ClassificationsDropdown"

// Define GROUPING_VALUES locally based on DashboardGrouping type
const GROUPING_VALUES: { label: string; value: DashboardGrouping }[] = [
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
  { label: "Hour", value: "hour" }, // Assuming hour is needed?
]

// Potentially needed hooks/utils (based on DashboardPage)
// import { usePageTitle } from "@/hooks/usePageTitle";
// import { useStatusOrganizations } from "@/hooks/useStatusOrganizations";
// import { useExportDashboardMutation } from "@/redux/services/organizationApi"; // If export needed

const AdminDashboardPage: FC = () => {
  // --- State ---
  const [openMobileFilters, setOpenMobileFilters] = useState(false)
  const isPolling = useRef(false)

  // --- Reusable Filter Logic ---
  // Note: useDashboard manages grouping, date range, classifications, campaigns etc.
  const {
    grouping,
    classificationsValue,
    selectedClassifications,
    from,
    to,
    multiple,
    indicatorsValue,
    isLoading: filtersLoading,
    isError: filtersError,
    // Add handlers from useDashboard needed for explicit controls
    handleChangeDate,
    handleGroupingChange,
  } = useDashboard()

  // --- Prepare Filters for Aggregated Query ---
  const dashboardFilters = useMemo<Omit<DashboardPayload, "id">>(() => {
    const filters: Omit<DashboardPayload, "id"> = {
      group: grouping,
      multiple: multiple === "true", // Ensure boolean
      classifications: Object.keys(selectedClassifications) ?? [],
      from: from ? formatDateToISOString(from) : undefined,
      to: to ? formatDateToISOString(to, "23:59:59.999") : undefined,
    }

    return filters
  }, [grouping, multiple, selectedClassifications, from, to])

  // --- Fetch Aggregated Dashboard Data ---
  // Simplify skip logic - only skip if essential filters are loading
  const skipQuery = filtersLoading // Adjust if other mandatory filters have loading states

  const {
    data: reporting,
    isLoading: reportingLoading,
    isFetching: reportingFetching,
    isError: reportingError,
  } = useGetAdminDashboardReportingQuery(
    {
      filters: dashboardFilters,
    },
    {
      skip: skipQuery,
      refetchOnMountOrArgChange: true, // Refetch if filters change
      pollingInterval: POLLING_INTERVAL,
      selectFromResult: (state) => ({
        ...state,
        isFetching: isPolling.current ? false : state.isFetching,
      }),
    },
  )

  // Effect for setting polling state
  useEffect(() => {
    if (!reportingFetching) {
      isPolling.current = false // Reset polling state when fetching completes
    }
    // Add a mechanism to set isPolling.current = true before manual refetch if needed
    // For interval polling, RTK Query handles it internally, but this flag helps UI
  }, [reportingFetching])

  // --- Combine Loading/Error States ---
  // Removed allOrgsLoading, activeOrgsLoading from isLoading
  // Removed allOrgsError from isError
  const isError = filtersError || (reportingError && !skipQuery)

  // --- Prepare Data for Components (similar to DashboardPage) ---
  // This logic assumes the aggregated 'reporting' data has the same structure
  const classificationLabels = classificationsValue.map(({ label }) => label)
  const indicatorLabels =
    indicatorsValue?.value?.map(({ label }) => label) ?? []
  const resultIndicators = classificationLabels.concat(indicatorLabels) ?? []

  // --- Render Logic ---
  if (isError) {
    return <ErrorMessage />
  }

  // Determine if we should show the loader vs content vs empty state
  const showLoader = reportingLoading && !isPolling.current
  // Show empty state if query is skipped (no orgs selected) OR if there's no reporting data after loading
  const showEmptyState =
    skipQuery ||
    (!reportingLoading &&
      !reportingFetching &&
      !reporting?.emails.data.length &&
      !reporting?.replies.data.length &&
      !reporting?.bounces?.data.length &&
      !getProviderAnalyticsRows(reporting).length)

  return (
    <>
      {/* Mobile Filters Drawer (Reuse) */}
      <MobileDashboardFiltersDrawer
        open={openMobileFilters}
        onClose={() => setOpenMobileFilters(false)}
      />

      <div
        className={cx("flex flex-col gap-5 h-full", {
          "justify-center items-center": showLoader && !reporting,
        })}
      >
        {/* Header Row with Filters/Actions - Always visible */}
        <div className="flex xl:justify-between items-center flex-wrap gap-3 w-full">
          {/* Mobile Filter Trigger */}
          <IconButton
            className="flex xl:hidden"
            onClick={() => setOpenMobileFilters(true)}
          >
            <FilterIcon className="w-6 h-6" />
          </IconButton>

          {/* Combine Desktop Filters and Org Selector */}
          <div className="flex flex-wrap items-center gap-3 flex-grow">
            {/* Date Pickers */}
            <div className="flex items-center">
              <DatePicker
                selected={from}
                onChange={handleChangeDate("from")}
                formatTextFieldValue={(v) => `${dayjs(v).format("M/D/YYYY")}`}
                inputClassName="!h-9 !w-[120px] sm:!w-36 rounded-md bg-white border-outline-dark border"
                suffixIcon={
                  <CalendarIcon className="w-5 h-5 opacity-60 text-black" />
                }
                maxDate={dayjs(to).toDate()}
              />
              <span className="opacity-60 text-sm leading-6 mx-2">to</span>
              <DatePicker
                selected={to}
                onChange={handleChangeDate("to")}
                formatTextFieldValue={(v) => `${dayjs(v).format("M/D/YYYY")}`}
                inputClassName="!h-9 !w-[120px] sm:!w-36 rounded-md bg-white border-outline-dark border"
                suffixIcon={
                  <CalendarIcon className="w-5 h-5 opacity-60 text-black" />
                }
                minDate={dayjs(from).toDate()}
              />
            </div>
            {/* Indicator and Classification Filters */}
            <IndicatorDropdown />
            <ClassificationsDropdown className="max-w-[350px]" />
            <AntSelect
              value={grouping}
              onChange={handleGroupingChange}
              options={GROUPING_VALUES}
              style={{ minWidth: 120 }}
            />
          </div>
          {/* Actions removed for brevity */}
        </div>

        {/* Content Area - Conditional Rendering */}
        <div className="flex flex-col flex-grow min-h-0">
          {showLoader ? (
            <Loader className="flex-1 self-center" />
          ) : showEmptyState ? (
            <EmptyGraphView indicators={resultIndicators} />
          ) : reporting ? ( // Only render graph if reporting data exists
            <div className="space-y-4">
              <ProviderAnalyticsSummary reporting={reporting} />
              {reporting.emails.data.length ||
              reporting.replies.data.length ||
              reporting.bounces?.data.length ? (
                <SingleGraphView
                  label="Aggregated Data"
                  reporting={reporting}
                  selectedIndicators={
                    indicatorsValue?.value?.reduce(
                      (acc, curr) => ({ ...acc, [curr.value]: true }),
                      {},
                    ) ?? {}
                  }
                  selectedClassifications={selectedClassifications}
                  from={from}
                  to={to}
                  grouping={grouping}
                  indicators={resultIndicators}
                />
              ) : (
                <EmptyGraphView indicators={resultIndicators} />
              )}
            </div>
          ) : (
            // Fallback for unexpected state where not loading, not empty, but no reporting data
            <div className="flex-1 flex items-center justify-center text-gray-500">
              No data available.
            </div>
          )}
          <div className="py-3 w-full"></div> {/* Padding at bottom */}
        </div>
      </div>
    </>
  )
}

// Add HOC for admin permission check if not handled by routing
// Example: export default withAdminPermission(AdminDashboardPage);
// Or ensure routing protects this page.
export default AdminDashboardPage
